import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './IntegratedSearch.css';
import { HotTable } from '@handsontable/react-wrapper';
import 'handsontable/styles/handsontable.css';
import 'handsontable/styles/ht-theme-main.css';
import {
    DownloadOutlined,
    HistoryOutlined,
    ReloadOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { Button, Space, Segmented, Switch, Card, Tooltip, Modal } from 'antd';
import { toast } from 'react-toastify';
import AuthService from '../../services/authService';
import ProtectedMenuPage from '../../components/ProtectedMenuPage/ProtectedMenuPage';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';
import { dispatchQuotaUpdated } from '../../utils/quotaEvent';

import { PUBLIC_API_BASE_URL } from '../../config/env';

const API_BASE = PUBLIC_API_BASE_URL;
const MAX_TARGET_RANK = 40;
const MAX_TARGET_COUNT = 20;
const COL_WIDTHS_STORAGE_KEY = 'integratedSearch_colWidths';
const DEFAULT_COL_WIDTHS = [200, 250, 140, 160, 110, 300, 130, 300, 110, 290];

const loadSavedColWidths = () => {
    try {
        const saved = localStorage.getItem(COL_WIDTHS_STORAGE_KEY);
        if (!saved) return null;
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) && parsed.length === DEFAULT_COL_WIDTHS.length ? parsed : null;
    } catch {
        return null;
    }
};

const saveColWidths = widths => {
    try {
        localStorage.setItem(COL_WIDTHS_STORAGE_KEY, JSON.stringify(widths));
    } catch (e) {
        console.warn('colWidths 저장 실패:', e);
    }
};

/** MainLayout 헤더 「가이드」버튼 → 본 페이지 전체 가이드 모달 */
const INTEGRATED_SEARCH_GUIDE_EVENT = 'tsad-open-integrated-search-guide';

const COLUMNS = [
    '순위키워드',
    'URL',
    '스토어명',
    '상품명',
    '단일 MID',
    '태그',
    '생성 키워드 갯수',
    '추출 키워드',
];

function IntegratedSearchInner() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [isAllSearching, setIsAllSearching] = useState(false);
    const [wordCount, setWordCount] = useState('2어절'); // Default to 2
    const integratedSearch = true;
    const includeAds = false;
    const [targetRank, setTargetRank] = useState(3);
    const [targetCount, setTargetCount] = useState(10);
    const [excludeStoreName, setExcludeStoreName] = useState(true);
    const [includeRankingKeyword, setIncludeRankingKeyword] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [isGuideOpen, setIsGuideOpen] = useState(false);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [historyPagination, setHistoryPagination] = useState({
        page: 1,
        totalPages: 1,
        total: 0,
    });
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [maxRows, setMaxRows] = useState(20);
    const [colWidths, setColWidths] = useState(() => {
        const saved = loadSavedColWidths();
        return saved ? [...saved] : [...DEFAULT_COL_WIDTHS];
    });
    const [isStopping, setIsStopping] = useState(false);
    const stopRef = useRef(false);
    const abortControllerRef = useRef(null);
    const rowControllersRef = useRef({}); // { rowId: AbortController }
    const hotRef = useRef(null);
    const tableWrapRef = useRef(null);
    const pasteGuardRef = useRef({ text: '', ts: 0 });

    useEffect(() => {
        const onOpenGuide = () => setIsGuideOpen(true);
        window.addEventListener(INTEGRATED_SEARCH_GUIDE_EVENT, onOpenGuide);
        return () => window.removeEventListener(INTEGRATED_SEARCH_GUIDE_EVENT, onOpenGuide);
    }, []);

    const fetchWithToken = async (url, options = {}) => {
        let token = AuthService.getToken();
        const headers = {
            ...(options.headers || {}),
            Authorization: `Bearer ${token}`,
        };
        let response = await fetch(url, { ...options, headers });

        if (response.status === 401) {
            if (!AuthService.getRefreshToken()) {
                AuthService.logout({ redirect: false });
                navigate('/login');
                throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
            }
            try {
                await AuthService.refreshAccessToken();
                token = AuthService.getToken();
                headers.Authorization = `Bearer ${token}`;
                response = await fetch(url, { ...options, headers });
            } catch {
                AuthService.logout({ redirect: false });
                navigate('/login');
                throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
            }
        }
        return response;
    };

    const handlePaste = useCallback(
        e => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            if (e.target?.closest?.('.handsontable')) return;

            const pasteData = e.clipboardData.getData('text');
            const now = Date.now();
            const last = pasteGuardRef.current;
            if (last.text === pasteData && now - last.ts < 400) return;
            pasteGuardRef.current = { text: pasteData, ts: now };

            e.preventDefault();
            const lines = pasteData.trim().split(/\r?\n/);

            const newRows = lines.map((line, index) => {
                const cells = line.split('\t').map(c => c.trim());
                const rowObj = {
                    id: Date.now() + index,
                    status: 'idle',
                    data: {},
                };

                COLUMNS.forEach(col => {
                    rowObj.data[col] = '';
                });
                // 엑셀 붙여넣기: 앞 두 열만 반영 (순위키워드, URL). 나머지 열은 비움.
                rowObj.data['순위키워드'] = cells[0] || '';
                rowObj.data['URL'] = cells[1] || '';

                return rowObj;
            });

            const firstCol = COLUMNS[0];
            const filteredRows = newRows.filter(row => {
                if (row.data[firstCol] === firstCol) return false;
                const u = (row.data.URL || '').trim();
                const kw = (row.data['순위키워드'] || '').trim();
                return kw !== '' || u !== '';
            });
            setRows(prev => {
                const space = maxRows - prev.length;
                if (space <= 0) {
                    toast.warning(`최대 ${maxRows}행까지 추가할 수 있습니다.`);
                    return prev;
                }
                const take = filteredRows.slice(0, space);
                if (filteredRows.length > space) {
                    toast.warning(
                        `등급별 최대 ${maxRows}행까지 가능합니다. ${space}행만 붙여넣었습니다.`,
                        { autoClose: 5000 }
                    );
                }
                return [...prev, ...take];
            });
        },
        [maxRows]
    );

    useEffect(() => {
        window.addEventListener('paste', handlePaste);
        return () => window.removeEventListener('paste', handlePaste);
    }, [handlePaste]);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await AuthService.authAxios.get('/settings/rank-limit', {
                    params: { gubun: 'integratedSearch' },
                });
                if (!alive || !data?.success) return;
                const n = parseInt(data.max_rows, 10);
                if (Number.isFinite(n) && n >= 1) setMaxRows(n);
            } catch {
                /* 기본 20 유지 */
            }
        })();
        return () => {
            alive = false;
        };
    }, []);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await AuthService.authAxios.get(
                    '/naver/quota/status/integratedSearch',
                    { params: { ensureRow: 1 } }
                );
                if (!alive || !data?.ok) return;
                dispatchQuotaUpdated('integratedSearch');
            } catch {
                /* 헤더 배지는 조회 실패 시 이전 값 유지 */
            }
        })();
        return () => {
            alive = false;
        };
    }, []);

    const searchRow = rowId => {
        const row = rows.find(r => r.id === rowId);
        if (row && row.status === 'loading') {
            stopRowSearch(rowId);
            return;
        }
        if (row && row.status === 'success') {
            Modal.confirm({
                title: '전체 재조회',
                content: (
                    <div>
                        <p>데이터를 모두 삭제하고 전체 재조회를 진행합니다.</p>
                        <p style={{ marginBottom: 0, color: '#64748b', fontSize: 13 }}>
                            키워드만 재조회는 「키워드만재조회」 버튼을 이용해 주세요.
                        </p>
                    </div>
                ),
                okText: '진행',
                cancelText: '취소',
                onOk: () => {
                    void callSearchApi(rowId, false);
                },
            });
            return;
        }
        callSearchApi(rowId, false);
    };

    const getFormattedDate = () => {
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        const d = String(now.getDate()).padStart(2, '0');
        const hh = String(now.getHours()).padStart(2, '0');
        const mm = String(now.getMinutes()).padStart(2, '0');
        const ss = String(now.getSeconds()).padStart(2, '0');
        return `${y}.${m}.${d} ${hh}:${mm}:${ss}`;
    };

    const clampOptionValue = (value, min, max) => {
        const n = parseInt(value, 10);
        if (!Number.isFinite(n)) return min;
        return Math.max(min, Math.min(max, n));
    };

    const searchKeywordsOnly = rowId => {
        const row = rows.find(r => r.id === rowId);
        if (row && row.keywordsDetail) {
            const remainingCount = (row.keywordsDetail.allPool || []).filter(
                k => !(row.keywordsDetail.allAttempted || []).includes(k)
            ).length;
            if (remainingCount === 0) {
                toast.warning('잔여 키워드가 없습니다. 새 풀 생성은 「조회」로 진행해 주세요.', {
                    autoClose: 5000,
                });
                return;
            }
        }
        callSearchApi(rowId, true);
    };

    const stopRowSearch = rowId => {
        const controller = rowControllersRef.current[rowId];
        if (controller) {
            controller.abort();
            delete rowControllersRef.current[rowId];
        }
        setRows(prev =>
            prev.map(r =>
                r.id === rowId
                    ? {
                          ...r,
                          status: 'idle',
                          subStatus: '중단됨',
                          searchMode: undefined,
                          keywordPhaseDone: undefined,
                      }
                    : r
            )
        );
    };

    const callSearchApi = async (rowId, onlyKeywords, extra = {}) => {
        const { batchQuotaToken } = extra;
        if (!batchQuotaToken) {
            try {
                const { data } = await AuthService.authAxios.get(
                    '/naver/quota/status/integratedSearch',
                    { params: { ensureRow: 1 } }
                );
                if (data?.ok && !data.unlimited && data.remaining <= 0) {
                    toast.error('오늘 통합 키워드 검색 한도를 모두 사용했습니다.');
                    return;
                }
            } catch {
                /* 네트워크 오류 시 서버 측 한도 검사에 맡김 */
            }
        }

        // 조회가 시작될 때 UI에서 이전 기록들을 즉시 비워줍니다.
        setRows(prev =>
            prev.map(r =>
                r.id === rowId
                    ? {
                          ...r,
                          status: 'loading',
                          searchMode: onlyKeywords ? 'keywords-only' : 'full',
                          keywordPhaseDone: Boolean(onlyKeywords),
                          data: !onlyKeywords
                              ? {
                                    ...r.data,
                                    태그: '',
                                    '생성 키워드 갯수': 0,
                                    '추출 키워드': '',
                                }
                              : r.data,
                          keywordsDetail: !onlyKeywords
                              ? {
                                    history: [], // 전체 조회 시 히스토리 초기화
                                }
                              : r.keywordsDetail,
                      }
                    : r
            )
        );

        // 개별 조회를 위한 AbortController 생성
        const controller = new AbortController();
        rowControllersRef.current[rowId] = controller;

        try {
            const row = rows.find(r => r.id === rowId);
            const response = await fetchWithToken(`${API_BASE}/integrated-search/search`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                signal: controller.signal,
                body: JSON.stringify({
                    rowData: row.data,
                    options: {
                        wordCount,
                        integratedSearch,
                        includeAds,
                        targetRank: clampOptionValue(targetRank, 1, MAX_TARGET_RANK),
                        targetCount: clampOptionValue(targetCount, 1, MAX_TARGET_COUNT),
                        excludeStoreName,
                        includeRankingKeyword,
                        onlyKeywordSearch: onlyKeywords,
                        existingPool: row.keywordsDetail?.allPool || [],
                        attemptedKeywords: row.keywordsDetail?.allAttempted || [],
                        ...(batchQuotaToken ? { batchQuotaToken } : {}),
                    },
                }),
            });

            if (!response.ok) {
                const errText = await response.text().catch(() => '');
                throw new Error(errText || `서버 오류 (${response.status})`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let result = null;

            let streamEnded = false;
            while (!streamEnded) {
                if (stopRef.current) {
                    await reader.cancel();
                    break;
                }
                const { done, value } = await reader.read();
                if (done) {
                    streamEnded = true;
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop(); // 마지막 미완성 라인은 홀딩

                for (const line of lines) {
                    if (!line.trim()) continue;
                    try {
                        const data = JSON.parse(line);

                        if (data.progress) {
                            setRows(prev =>
                                prev.map(r => {
                                    if (r.id !== rowId) return r;
                                    const keywordPhaseDone =
                                        r.searchMode === 'full' &&
                                        (data.step === 'searching' || data.step === 'saving')
                                            ? true
                                            : r.keywordPhaseDone;
                                    return {
                                        ...r,
                                        subStatus: data.message,
                                        keywordPhaseDone,
                                    };
                                })
                            );
                        } else {
                            // 최종 결과
                            result = data;
                        }
                    } catch (e) {
                        console.error('JSON parsing error during streaming:', e);
                    }
                }
            }

            // 최종 줄인 바닥 처리
            if (buffer.trim()) {
                try {
                    const finalData = JSON.parse(buffer);
                    if (!finalData.progress) result = finalData;
                } catch {
                    /* 스트림 마지막 조각이 불완전한 JSON일 수 있음 */
                }
            }

            if (result && result.success) {
                dispatchQuotaUpdated('integratedSearch');
                setRows(prev =>
                    prev.map(r =>
                        r.id === rowId
                            ? {
                                  ...r,
                                  status: 'success',
                                  subStatus: '',
                                  searchMode: undefined,
                                  keywordPhaseDone: undefined,
                                  data: {
                                      ...r.data,
                                      스토어명:
                                          result.results?.brandName ||
                                          result.results?.storeName ||
                                          r.data['스토어명'],
                                      상품명: result.results?.productTitle || r.data['상품명'],
                                      '단일 MID': result.results?.productId || r.data['단일 MID'],
                                      태그: result.results?.seoInfo?.sellerTags
                                          ? result.results.seoInfo.sellerTags
                                                .map(t => t.text)
                                                .join(', ')
                                          : Array.isArray(result.results?.tags)
                                            ? result.results.tags.join(', ')
                                            : '',
                                      '생성 키워드 갯수':
                                          result.results?.totalGeneratedCount ||
                                          result.results?.generatedKeywordsCount ||
                                          0,
                                      '추출 키워드': result.results?.finalFoundKeywords
                                          ? result.results.finalFoundKeywords
                                                .map(k => k.keyword)
                                                .join(', ')
                                          : '',
                                  },
                                  keywordsDetail: {
                                      allPool: result.results?.allGeneratedPool || [],
                                      used: result.results?.usedKeywords || [],
                                      found: result.results?.finalFoundKeywords || [],
                                      allAttempted: result.results?.allAttemptedKeywords || [],
                                      generatedKeywordsCount:
                                          result.results?.totalGeneratedCount || 0,
                                      matchingCount: result.results?.matchingCount || 0,
                                      availableKeywordsCount:
                                          result.results?.availableKeywordsCount || 0,
                                      attemptedKeywordsCount:
                                          result.results?.attemptedKeywordsCount || 0,
                                      usedKeywordsCount: result.results?.usedKeywordsCount || 0,
                                      usedKeywords: result.results?.usedKeywords || [],
                                      history: [
                                          ...(r.keywordsDetail?.history || []),
                                          {
                                              count: (result.results?.finalFoundKeywords || [])
                                                  .length,
                                              timestamp: getFormattedDate(),
                                              keywords: (result.results?.finalFoundKeywords || [])
                                                  .map(k => `${k.keyword}(${k.rank}위)`)
                                                  .join(', '),
                                          },
                                      ],
                                  },
                              }
                            : r
                    )
                );
            } else {
                toast.error((result && result.message) || '조회 중 오류가 발생했습니다.', {
                    autoClose: 6000,
                });
                setRows(prev =>
                    prev.map(r =>
                        r.id === rowId
                            ? {
                                  ...r,
                                  status: 'idle',
                                  subStatus: '',
                                  searchMode: undefined,
                                  keywordPhaseDone: undefined,
                              }
                            : r
                    )
                );
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Search aborted for row:', rowId);
                return;
            }
            console.error('Search error:', error);
            toast.error(error.message || '서버 연결 오류가 발생했습니다.', {
                autoClose: 6000,
            });
            setRows(prev =>
                prev.map(r =>
                    r.id === rowId
                        ? {
                              ...r,
                              status: 'idle',
                              subStatus: '',
                              searchMode: undefined,
                              keywordPhaseDone: undefined,
                          }
                        : r
                )
            );
        } finally {
            delete rowControllersRef.current[rowId];
        }
    };

    const searchSelected = async () => {
        const selectedRows = rows.filter(r => {
            const kw = String(r?.data?.['순위키워드'] || '').trim();
            const url = String(r?.data?.URL || '').trim();
            return kw !== '' || url !== '';
        });
        if (selectedRows.length === 0) {
            toast.warning('검색할 항목을 입력해 주세요.');
            return;
        }
        if (selectedRows.length > maxRows) {
            toast.warning(`등급별 최대 ${maxRows}행까지 선택 조회할 수 있습니다.`);
            return;
        }

        const runBatchSearch = async () => {
            setIsAllSearching(true);
            setIsStopping(false);
            stopRef.current = false;
            abortControllerRef.current = new AbortController();
            setProgress({ current: 0, total: selectedRows.length });

            const targetIds = new Set(selectedRows.map(r => r.id));
            setRows(prev =>
                prev.map(r =>
                    targetIds.has(r.id)
                        ? {
                              ...r,
                              status: 'loading',
                              subStatus: '대기 중...',
                              searchMode: 'full',
                              keywordPhaseDone: false,
                          }
                        : r
                )
            );

            const resetBatchLoading = () => {
                setRows(prev =>
                    prev.map(r =>
                        targetIds.has(r.id)
                            ? {
                                  ...r,
                                  status: 'idle',
                                  subStatus: '',
                                  searchMode: undefined,
                                  keywordPhaseDone: undefined,
                              }
                            : r
                    )
                );
            };

            let batchQuotaToken = null;
            if (selectedRows.length >= 2) {
                const beginRes = await fetchWithToken(`${API_BASE}/integrated-search/begin-batch`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ rowCount: selectedRows.length }),
                });
                let beginJson = null;
                try {
                    beginJson = await beginRes.json();
                } catch {
                    beginJson = null;
                }
                if (!beginRes.ok || !beginJson?.success) {
                    toast.error(
                        (beginJson && beginJson.message) ||
                            '배치 조회를 시작할 수 없습니다. 한도를 확인해 주세요.',
                        { autoClose: 6000 }
                    );
                    resetBatchLoading();
                    setIsAllSearching(false);
                    setIsStopping(false);
                    abortControllerRef.current = null;
                    setTimeout(() => setProgress({ current: 0, total: 0 }), 500);
                    return;
                }
                batchQuotaToken = beginJson.batchQuotaToken;
                dispatchQuotaUpdated('integratedSearch');
            }

            /** 이전 통합검색(tsad_IntegratedSearch)과 동일: 최대 3워커가 행을 나눠 병렬 조회. 배치 토큰은 모든 워커가 공유(한도 1회). */
            const CONCURRENCY = 3;
            let currentIndex = 0;
            let completedCount = 0;

            const runWorker = async () => {
                while (currentIndex < selectedRows.length && !stopRef.current) {
                    const row = selectedRows[currentIndex++];
                    await callSearchApi(row.id, false, batchQuotaToken ? { batchQuotaToken } : {});
                    completedCount += 1;
                    setProgress(prev => ({
                        ...prev,
                        current: Math.min(prev.total, completedCount),
                    }));
                }
            };

            const workerCount = Math.min(CONCURRENCY, selectedRows.length);
            const workers = [];
            for (let w = 0; w < workerCount; w++) {
                workers.push(runWorker());
            }
            await Promise.all(workers);

            setIsAllSearching(false);
            setIsStopping(false);
            abortControllerRef.current = null;
            if (stopRef.current) {
                toast.info('중단 요청에 의해 검색이 중지되었습니다.', { autoClose: 5000 });
            }
            setTimeout(() => setProgress({ current: 0, total: 0 }), 3000);
        };

        const rowsWithData = selectedRows.filter(r => r.status === 'success');
        if (rowsWithData.length > 0) {
            Modal.confirm({
                title: '이미 조회된 항목 포함',
                content: (
                    <div>
                        <p>
                            다음 항목은 이미 검색된 데이터가 있습니다. 모두 초기화 후 전체
                            재조회합니다.
                        </p>
                    </div>
                ),
                okText: '재조회 진행',
                cancelText: '취소',
                width: 480,
                // Promise를 반환하면 Ant Design이 배치 완료까지 모달을 닫지 않음 → 즉시 닫고 백그라운드 실행
                onOk: () => {
                    void runBatchSearch();
                },
            });
            return;
        }

        await runBatchSearch();
    };

    const handleStopSearch = () => {
        setIsStopping(true);
        stopRef.current = true;
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
    };

    const downloadExcel = () => {
        if (rows.length === 0) return;

        // 제외할 컬럼 목록
        const excludedCols = ['태그', '생성 키워드 갯수'];
        const filteredCols = COLUMNS.filter(col => !excludedCols.includes(col));
        const header = [...filteredCols, '상태'];

        // CSV 데이터 생성 (Excel에서 날짜 변형 방지를 위해 식 구문 사용 ="값")
        const csvRows = [header.join(',')];

        rows.forEach(row => {
            const rowData = filteredCols.map(col => {
                const val = row.data[col] || '';
                return `"${String(val).replace(/"/g, '""')}"`;
            });

            const statusText =
                row.status === 'success' ? '완료' : row.status === 'loading' ? '조회중' : '대기';
            rowData.push(`"${statusText}"`);
            csvRows.push(rowData.join(','));
        });

        const csvContent = '\ufeff' + csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `검색결과_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const clearRows = () => {
        setRows([]);
    };

    const deleteRow = rowId => {
        setRows(prev => prev.filter(r => r.id !== rowId));
    };

    const openKeywordsModal = row => {
        setModalData(row); // Pass the whole row object
        setIsModalOpen(true);
    };

    const displayRows = useMemo(() => {
        const cap = Math.max(0, Number(maxRows) || 0);
        if (rows.length >= cap) return rows;

        const emptyData = {};
        COLUMNS.forEach(col => {
            emptyData[col] = '';
        });

        const placeholders = Array.from({ length: cap - rows.length }, (_, i) => ({
            id: `__placeholder_${i}`,
            status: 'idle',
            data: { ...emptyData },
            __placeholder: true,
        }));
        return [...rows, ...placeholders];
    }, [rows, maxRows]);

    const hotColHeaders = useMemo(
        () => [
            '순위키워드 <span class="integrated-search-required-star">*</span>',
            'URL <span class="integrated-search-required-star">*</span>',
            ...COLUMNS.slice(2),
            '상태',
            '관리',
        ],
        []
    );

    const hotData = useMemo(
        () =>
            displayRows.map(row => {
                return [...COLUMNS.map(col => row.data[col] || ''), '', ''];
            }),
        [displayRows]
    );

    const createEmptyRow = useCallback((idSeed = 0) => {
        const data = {};
        COLUMNS.forEach(col => {
            data[col] = '';
        });
        return {
            id: `${Date.now()}_${idSeed}_${Math.random().toString(36).slice(2, 7)}`,
            status: 'idle',
            data,
        };
    }, []);

    const insertRowBySelection = useCallback(
        (where, hotInstance) => {
            setRows(prev => {
                if (prev.length >= maxRows) {
                    toast.warning(`최대 ${maxRows}행까지 추가할 수 있습니다.`);
                    return prev;
                }
                const sel = hotInstance?.getSelectedLast?.();
                const anchorRow = Array.isArray(sel) ? Number(sel[0]) : prev.length - 1;
                const safeAnchor = Number.isFinite(anchorRow)
                    ? Math.max(0, Math.min(anchorRow, prev.length))
                    : prev.length;
                const insertIndex = where === 'above' ? safeAnchor : safeAnchor + 1;
                const next = [...prev];
                next.splice(Math.min(insertIndex, next.length), 0, createEmptyRow(next.length));
                return next.slice(0, maxRows);
            });
        },
        [createEmptyRow, maxRows]
    );

    const removeSelectedRows = useCallback(hotInstance => {
        const sel = hotInstance?.getSelectedLast?.();
        if (!Array.isArray(sel)) return;
        const [r1, , r2] = sel;
        const start = Math.max(0, Math.min(Number(r1), Number(r2)));
        const end = Math.max(0, Math.max(Number(r1), Number(r2)));
        if (!Number.isFinite(start) || !Number.isFinite(end)) return;
        setRows(prev => prev.filter((_, idx) => idx < start || idx > end));
    }, []);

    const hotColumns = useMemo(
        () => [
            { className: 'integrated-search-col-required htLeft' }, // 순위키워드
            { className: 'integrated-search-col-required htLeft' }, // URL
            { readOnly: true }, // 스토어명
            { readOnly: true }, // 상품명
            { readOnly: true }, // 단일 MID
            { readOnly: true }, // 태그
            { readOnly: true, className: 'integrated-search-col-kw-count' }, // 생성 키워드 갯수
            { readOnly: true }, // 추출 키워드
            { readOnly: true }, // 상태
            { readOnly: true }, // 관리
        ],
        []
    );

    const hotStatusRenderer = useCallback(
        (instance, td, row) => {
            td.innerHTML = '';
            const rowObj = displayRows[row];
            if (!rowObj || rowObj.__placeholder) return td;
            const kw = String(rowObj.data?.['순위키워드'] || '').trim();
            const url = String(rowObj.data?.URL || '').trim();
            if (kw === '' && url === '') return td;

            const wrap = document.createElement('div');
            wrap.className = 'status-container';

            const badge = document.createElement('span');
            badge.className = `status-badge ${rowObj.status}`;
            badge.textContent =
                rowObj.status === 'success'
                    ? '완료'
                    : rowObj.status === 'loading'
                      ? '진행 중'
                      : '대기';
            wrap.appendChild(badge);

            if (rowObj.status === 'loading' && rowObj.subStatus) {
                const sub = document.createElement('div');
                sub.className = 'sub-status-text';
                sub.textContent = rowObj.subStatus;
                wrap.appendChild(sub);
            }

            td.appendChild(wrap);
            return td;
        },
        [displayRows]
    );

    const hotManageRenderer = useCallback(
        (instance, td, row) => {
            td.innerHTML = '';
            const rowObj = displayRows[row];
            if (!rowObj || rowObj.__placeholder) return td;
            const kw = String(rowObj.data?.['순위키워드'] || '').trim();
            const url = String(rowObj.data?.URL || '').trim();
            if (kw === '' && url === '') return td;

            const actions = document.createElement('div');
            actions.className = 'mgmt-actions';

            const mkBtn = (cls, label, action, disabled = false) => {
                const b = document.createElement('button');
                b.className = cls;
                b.textContent = label;
                b.dataset.action = action;
                b.disabled = disabled;
                return b;
            };

            actions.appendChild(
                mkBtn(
                    `btn-row-search ${rowObj.status === 'loading' ? 'loading stopping' : ''}`,
                    rowObj.status === 'loading' ? '중지' : '조회',
                    'search',
                    false
                )
            );
            actions.appendChild(
                mkBtn(
                    'btn-row-keywords-only',
                    '키워드만재조회',
                    'keywordsOnly',
                    rowObj.status === 'loading'
                )
            );
            actions.appendChild(
                mkBtn(
                    'btn-row-keywords',
                    '생성키워드',
                    'keywords',
                    rowObj.status === 'idle' || rowObj.status === 'loading'
                )
            );
            actions.appendChild(
                mkBtn('btn-row-delete', '삭제', 'delete', rowObj.status === 'loading')
            );

            td.appendChild(actions);
            return td;
        },
        [displayRows]
    );

    const syncRightStickyOffsets = useCallback(() => {
        const hotInstance = hotRef.current?.hotInstance;
        const tableWrap = tableWrapRef.current;
        if (!hotInstance || !tableWrap) return;
        const statusColWidth = Number(hotInstance.getColWidth(8)) || 110;
        const manageColWidth = Number(hotInstance.getColWidth(9)) || 290;
        const wtHolder = tableWrap.querySelector('.handsontable .wtHolder');
        const scrollbarWidth =
            wtHolder && wtHolder.offsetWidth > wtHolder.clientWidth
                ? wtHolder.offsetWidth - wtHolder.clientWidth
                : 0;
        tableWrap.style.setProperty('--integrated-status-col-w', `${statusColWidth}px`);
        tableWrap.style.setProperty('--integrated-manage-col-w', `${manageColWidth}px`);
        tableWrap.style.setProperty('--integrated-scrollbar-w', `${scrollbarWidth}px`);
    }, []);

    const handleHotAfterChange = useCallback(
        (changes, source) => {
            if (!changes || source === 'loadData') return;

            for (const [rowIndexRaw, colIndexRaw, oldValue, newValue] of changes) {
                const rowIndex = Number(rowIndexRaw);
                const colIndex = Number(colIndexRaw);
                if (!Number.isFinite(rowIndex) || !Number.isFinite(colIndex)) continue;

                // 순위키워드(0), URL(1)만 편집 허용
                if (colIndex !== 0 && colIndex !== 1) continue;
                if (oldValue === newValue) continue;

                const key = colIndex === 0 ? '순위키워드' : 'URL';
                setRows(prev => {
                    const next = [...prev];
                    while (next.length <= rowIndex && next.length < maxRows) {
                        next.push(createEmptyRow(next.length));
                    }
                    if (!next[rowIndex]) return prev;
                    next[rowIndex] = {
                        ...next[rowIndex],
                        data: {
                            ...next[rowIndex].data,
                            [key]: newValue == null ? '' : String(newValue),
                        },
                    };
                    return next;
                });
            }
        },
        [maxRows, createEmptyRow]
    );

    const openHistoryModal = async (page = 1) => {
        try {
            const res = await fetchWithToken(
                `${API_BASE}/integrated-search/history?page=${page}&limit=15`,
                { method: 'GET' }
            );
            const data = await res.json();
            if (data.success) {
                setHistoryData(data.history);
                setHistoryPagination(data.pagination);
                setIsHistoryOpen(true);
            } else {
                toast.error(data.message || '히스토리를 불러오지 못했습니다.');
            }
        } catch (err) {
            toast.error('히스토리를 불러오는데 실패했습니다.');
        }
    };

    return (
        <div className="integrated-search-page app-container integrated-search-adhelp">
            <div className="page-content-inner integrated-search-inner">
                {progress.total > 0 && (
                    <div className="progress-container">
                        <div className="progress-main">
                            <div className="progress-info">
                                <span className="progress-text">
                                    {isStopping
                                        ? '중지 중 - 현재 배치 완료 후 멈춥니다...'
                                        : `전체 검색 중... (${progress.current}/${progress.total})`}
                                </span>
                                <span className="progress-percent">
                                    {Math.round((progress.current / progress.total) * 100)}%
                                </span>
                            </div>
                            <div className="progress-bar-bg">
                                <div
                                    className="progress-bar-fill"
                                    style={{
                                        width: `${(progress.current / progress.total) * 100}%`,
                                        backgroundColor: isStopping
                                            ? '#ef4444'
                                            : 'var(--primary-color)',
                                    }}
                                />
                            </div>
                        </div>
                        <button
                            className={`btn-stop ${isStopping ? 'stopping' : ''}`}
                            onClick={handleStopSearch}
                            disabled={isStopping}
                        >
                            {isStopping ? '중지 중...' : '중지'}
                        </button>
                    </div>
                )}

                <Card
                    className="control-card integrated-search-toolbar-card"
                    bodyStyle={{ padding: '8px 16px' }}
                >
                    <div className="integrated-search-toolbar-inner">
                        <div className="integrated-search-toolbar-left">
                            <span className="integrated-search-toolbar-label">조회 옵션</span>
                            <Segmented
                                className="integrated-search-segmented"
                                size="middle"
                                value={wordCount}
                                onChange={v => setWordCount(v)}
                                options={['2어절', '3어절']}
                            />
                            <div className="integrated-search-switch-row">
                                <span className="integrated-search-switch-label">
                                    순위키워드 포함
                                </span>
                                <Switch
                                    checked={includeRankingKeyword}
                                    onChange={setIncludeRankingKeyword}
                                />
                            </div>
                            <div className="integrated-search-switch-row">
                                <span className="integrated-search-switch-label">
                                    스토어명 제외
                                </span>
                                <Switch checked={excludeStoreName} onChange={setExcludeStoreName} />
                            </div>
                            <div className="integrated-search-extract-row">
                                <span className="integrated-search-toolbar-label integrated-search-extract-label">
                                    추출 조건
                                </span>
                                <input
                                    type="number"
                                    className="integrated-search-num-input"
                                    value={targetRank}
                                    onChange={e =>
                                        setTargetRank(
                                            clampOptionValue(e.target.value, 1, MAX_TARGET_RANK)
                                        )
                                    }
                                    min={1}
                                    max={MAX_TARGET_RANK}
                                    aria-label="목표 순위"
                                />
                                <span className="integrated-search-unit">위 이내</span>
                                <input
                                    type="number"
                                    className="integrated-search-num-input"
                                    value={targetCount}
                                    onChange={e =>
                                        setTargetCount(
                                            clampOptionValue(e.target.value, 1, MAX_TARGET_COUNT)
                                        )
                                    }
                                    min={1}
                                    max={MAX_TARGET_COUNT}
                                    aria-label="추출 개수"
                                />
                                <span className="integrated-search-unit">개 추출</span>
                                <span className="integrated-search-cap-sep" aria-hidden>
                                    |
                                </span>
                                <span className="integrated-search-addable-cap">
                                    <span className="integrated-search-addable-label">
                                        추가가능행수
                                    </span>
                                    <span className="integrated-search-addable-num">{maxRows}</span>
                                </span>
                            </div>
                        </div>
                        <div className="integrated-search-toolbar-right">
                            <Space size="small" wrap className="integrated-search-action-space">
                                <Button
                                    type="primary"
                                    className="integrated-search-run-btn"
                                    icon={<SearchOutlined />}
                                    onClick={searchSelected}
                                    disabled={isAllSearching || rows.length === 0}
                                    loading={isAllSearching}
                                >
                                    {isAllSearching ? '조회 진행 중...' : '조회시작'}
                                </Button>
                                <Button
                                    className="page-action-btn"
                                    icon={<ReloadOutlined />}
                                    onClick={clearRows}
                                >
                                    초기화
                                </Button>
                                <Button
                                    className="history-btn"
                                    icon={<HistoryOutlined />}
                                    onClick={() => openHistoryModal(1)}
                                >
                                    조회기록
                                </Button>
                                <Tooltip title="엑셀 다운로드">
                                    <span className="integrated-search-excel-tooltip-wrap">
                                        <Button
                                            className="excel-button"
                                            icon={<DownloadOutlined />}
                                            onClick={downloadExcel}
                                            disabled={rows.length === 0}
                                            aria-label="엑셀 다운로드"
                                        />
                                    </span>
                                </Tooltip>
                            </Space>
                        </div>
                    </div>
                </Card>

                <main ref={tableWrapRef} className="table-wrapper integrated-search-table-wrap">
                    <HotTable
                        ref={hotRef}
                        className="integrated-search-hot ht-theme-main"
                        data={hotData}
                        colHeaders={hotColHeaders}
                        columns={hotColumns.map((c, idx) => {
                            if (idx === 8) return { ...c, renderer: hotStatusRenderer };
                            if (idx === 9) return { ...c, renderer: hotManageRenderer };
                            return c;
                        })}
                        rowHeaders={true}
                        stretchH="none"
                        width="100%"
                        height="100%"
                        colWidths={colWidths}
                        rowHeights={28}
                        columnHeaderHeight={34}
                        autoWrapRow={false}
                        autoWrapCol={false}
                        manualColumnResize={true}
                        contextMenu={{
                            items: {
                                copy: { name: '복사' },
                                cut: { name: '잘라내기' },
                                row_above_custom: {
                                    name: '위에 행 삽입',
                                    callback: function () {
                                        insertRowBySelection('above', this);
                                    },
                                },
                                row_below_custom: {
                                    name: '아래에 행 삽입',
                                    callback: function () {
                                        insertRowBySelection('below', this);
                                    },
                                },
                                remove_row_custom: {
                                    name: '행 삭제',
                                    callback: function () {
                                        removeSelectedRows(this);
                                    },
                                },
                            },
                        }}
                        fixedColumnsStart={0}
                        fixedColumnsEnd={0}
                        licenseKey="non-commercial-and-evaluation"
                        afterChange={handleHotAfterChange}
                        beforePaste={(data, coords) => {
                            if (!Array.isArray(data) || data.length === 0) return false;
                            if (!Array.isArray(coords) || coords.length === 0) return false;

                            const text = data.map(row => row.join('\t')).join('\n');
                            const now = Date.now();
                            const last = pasteGuardRef.current;
                            if (last.text === text && now - last.ts < 400) return false;
                            pasteGuardRef.current = { text, ts: now };

                            const startRowRaw = coords?.startRow ?? coords?.[0]?.startRow;
                            const startColRaw = coords?.startCol ?? coords?.[0]?.startCol;
                            const startRow = Number.isFinite(Number(startRowRaw))
                                ? Math.max(0, Number(startRowRaw))
                                : 0;
                            const startCol = Number.isFinite(Number(startColRaw))
                                ? Number(startColRaw)
                                : 0;

                            const rowsFromPaste = data.map((cells, index) => {
                                const rowObj = {
                                    id: Date.now() + index,
                                    status: 'idle',
                                    data: {},
                                    _pasteHasKw: false,
                                    _pasteHasUrl: false,
                                };

                                // 붙여넣기 시작 컬럼(startCol) 기준으로 실제 테이블 컬럼에 맞게 매핑합니다.
                                // - 실제 테이블 col 0 => 순위키워드
                                // - 실제 테이블 col 1 => URL
                                for (let j = 0; j < cells.length; j++) {
                                    const actualCol = startCol + j;
                                    const v = (cells?.[j] ?? '').toString().trim();
                                    if (actualCol === 0) {
                                        rowObj.data['순위키워드'] = v;
                                        rowObj._pasteHasKw = true;
                                    }
                                    if (actualCol === 1) {
                                        rowObj.data.URL = v;
                                        rowObj._pasteHasUrl = true;
                                    }
                                }

                                return rowObj;
                            });

                            const firstCol = COLUMNS[0];
                            const filteredRows = rowsFromPaste.filter(row => {
                                if (row.data[firstCol] === firstCol) return false;
                                const u = (row.data.URL || '').trim();
                                const kw = (row.data['순위키워드'] || '').trim();
                                return kw !== '' || u !== '';
                            });

                            setRows(prev => {
                                const targetEndExclusive = startRow + filteredRows.length;
                                if (startRow >= maxRows) {
                                    toast.warning(`최대 ${maxRows}행까지 추가할 수 있습니다.`);
                                    return prev;
                                }
                                const allowedCount = Math.max(0, maxRows - startRow);
                                const take = filteredRows.slice(0, allowedCount);
                                if (filteredRows.length > allowedCount) {
                                    toast.warning(
                                        `등급별 최대 ${maxRows}행까지 가능합니다. ${allowedCount}행만 붙여넣었습니다.`,
                                        { autoClose: 5000 }
                                    );
                                }

                                const next = [...prev];
                                const needLength = Math.min(
                                    maxRows,
                                    Math.max(next.length, targetEndExclusive)
                                );
                                while (next.length < needLength) {
                                    next.push(createEmptyRow(next.length));
                                }

                                take.forEach((row, idx) => {
                                    const targetIndex = startRow + idx;
                                    if (targetIndex >= maxRows || !next[targetIndex]) return;

                                    const shouldUpdateKw = Boolean(row._pasteHasKw);
                                    const shouldUpdateUrl = Boolean(row._pasteHasUrl);
                                    next[targetIndex] = {
                                        ...next[targetIndex],
                                        data: {
                                            ...next[targetIndex].data,
                                            ...(shouldUpdateKw
                                                ? {
                                                      순위키워드: row.data['순위키워드'] || '',
                                                  }
                                                : {}),
                                            ...(shouldUpdateUrl ? { URL: row.data.URL || '' } : {}),
                                        },
                                    };
                                });

                                return next.slice(0, maxRows);
                            });
                            return false;
                        }}
                        afterRender={syncRightStickyOffsets}
                        afterColumnResize={(newSize, column) => {
                            setColWidths(prev => {
                                const next = [...prev];
                                next[column] = newSize;
                                saveColWidths(next);
                                return next;
                            });
                            syncRightStickyOffsets();
                        }}
                        cells={(row, col) => {
                            if (col === 0 || col === 1) {
                                return {
                                    readOnly: false,
                                    className: 'integrated-search-col-required htLeft',
                                };
                            }
                            if (col === 6) {
                                return {
                                    readOnly: true,
                                    className: 'integrated-search-col-kw-count',
                                };
                            }
                            return { readOnly: true };
                        }}
                        afterOnCellMouseDown={(event, coords) => {
                            if (!coords || coords.row < 0 || coords.col !== 9) return;
                            const target = event?.target;
                            if (!target || typeof target.closest !== 'function') return;
                            const btn = target.closest('button[data-action]');
                            if (!btn || btn.disabled) return;

                            const rowObj = displayRows[coords.row];
                            if (!rowObj || rowObj.__placeholder) return;

                            const action = btn.dataset.action;
                            if (action === 'search') searchRow(rowObj.id);
                            if (action === 'keywordsOnly') searchKeywordsOnly(rowObj.id);
                            if (action === 'keywords') openKeywordsModal(rowObj);
                            if (action === 'delete') deleteRow(rowObj.id);
                        }}
                    />
                </main>

                {isModalOpen && modalData && (
                    <div
                        className="modal-overlay"
                        role="button"
                        tabIndex={0}
                        aria-label="닫기"
                        onClick={e => {
                            if (e.target === e.currentTarget) setIsModalOpen(false);
                        }}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
                                e.preventDefault();
                                setIsModalOpen(false);
                            }
                        }}
                    >
                        <div className="modal-content">
                            <div className="modal-header">
                                <h2>키워드 상세 정보</h2>
                                <button className="btn-close" onClick={() => setIsModalOpen(false)}>
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="modal-summary">
                                    <div className="summary-item">
                                        <span className="label">전체 키워드 풀</span>
                                        <span className="value">
                                            {modalData.keywordsDetail?.allPool?.length || 0}개
                                        </span>
                                    </div>
                                    <div className="summary-item status-checked">
                                        <span className="label">시도(체크)한 키워드</span>
                                        <span className="value">
                                            {modalData.keywordsDetail?.allAttempted?.length || 0}개
                                        </span>
                                    </div>
                                    <div className="summary-item status-found">
                                        <span className="label">실제 추출된 키워드(누적) ★</span>
                                        <span className="value">
                                            {modalData.keywordsDetail?.used?.length || 0}개
                                        </span>
                                    </div>
                                    <div className="summary-item status-waiting">
                                        <span className="label">남은(대기) 키워드</span>
                                        <span className="value">
                                            {modalData.keywordsDetail?.allPool?.filter(
                                                k =>
                                                    !modalData.keywordsDetail.allAttempted?.includes(
                                                        k
                                                    )
                                            ).length || 0}
                                            개
                                        </span>
                                    </div>
                                </div>

                                {modalData.keywordsDetail?.history?.length > 0 && (
                                    <div className="history-section">
                                        <h3 className="history-title">
                                            📊 누적 조회 내역 (History)
                                        </h3>
                                        <div className="history-list">
                                            {modalData.keywordsDetail.history.map((h, idx) => (
                                                <div key={idx} className="history-item">
                                                    <div className="history-item-header">
                                                        <span className="history-item-round">
                                                            {idx + 1}차 조회 ({h.count}개 추출)
                                                        </span>
                                                        <span className="history-item-time">
                                                            {h.timestamp}
                                                        </span>
                                                    </div>
                                                    <div className="history-item-keywords">
                                                        {h.keywords || '추출된 키워드 없음'}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="keyword-section">
                                    <h3 className="section-title found">
                                        1. 실제 추출된 키워드 (Target Rank 안) ★
                                    </h3>
                                    <div className="keyword-tags">
                                        {modalData.keywordsDetail?.used?.map((k, i) => (
                                            <span key={i} className="k-tag used">
                                                {k.keyword} ({k.rank}위)
                                            </span>
                                        ))}
                                        {(!modalData.keywordsDetail?.used ||
                                            modalData.keywordsDetail.used.length === 0) && (
                                            <span className="empty-msg">
                                                아직 추출된 키워드가 없습니다.
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="keyword-section">
                                    <h3 className="section-title attempted">
                                        2. 시도했으나 제외된 키워드 (Target Rank 밖)
                                    </h3>
                                    <div className="keyword-tags">
                                        {(() => {
                                            const att =
                                                modalData.keywordsDetail?.allAttempted || [];
                                            const used = modalData.keywordsDetail?.used || [];
                                            const excluded = att.filter(
                                                ak => !used.some(uk => uk.keyword === ak)
                                            );
                                            if (att.length === 0) {
                                                return (
                                                    <span className="empty-msg">
                                                        아직 순위 조회를 시도한 키워드가 없습니다.
                                                    </span>
                                                );
                                            }
                                            if (excluded.length === 0) {
                                                return (
                                                    <span className="empty-msg">
                                                        제외된 키워드가 없습니다.
                                                    </span>
                                                );
                                            }
                                            return excluded.map((k, i) => (
                                                <span key={i} className="k-tag attempted">
                                                    {k}
                                                </span>
                                            ));
                                        })()}
                                    </div>
                                </div>

                                <div className="keyword-section">
                                    <h3 className="section-title available">
                                        3. 다음 조회 시 사용될 키워드 대기열 (Remaining)
                                    </h3>
                                    <div className="keyword-tags">
                                        {modalData.keywordsDetail?.allPool
                                            ?.filter(
                                                k =>
                                                    !modalData.keywordsDetail.allAttempted.includes(
                                                        k
                                                    )
                                            )
                                            .map((k, i) => (
                                                <span key={i} className="k-tag available">
                                                    {k}
                                                </span>
                                            ))}
                                        {(!modalData.keywordsDetail?.allPool ||
                                            modalData.keywordsDetail.allPool.length === 0) && (
                                            <span className="empty-msg">
                                                키워드 풀이 비어있습니다.
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {isGuideOpen && (
                    <div
                        className="modal-overlay"
                        role="button"
                        tabIndex={0}
                        aria-label="닫기"
                        onClick={e => {
                            if (e.target === e.currentTarget) setIsGuideOpen(false);
                        }}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
                                e.preventDefault();
                                setIsGuideOpen(false);
                            }
                        }}
                    >
                        <div className="modal-content guide-modal">
                            <div className="modal-header">
                                <h2>Integrated Search PRO 서비스 가이드</h2>
                                <button className="btn-close" onClick={() => setIsGuideOpen(false)}>
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="guide-section">
                                    <h3>
                                        🚀 핵심 기술: 키워드 생성 및 네이버쇼핑 통합 검색 순위 조회
                                    </h3>
                                    <ul className="guide-list">
                                        <li>
                                            <strong>AI 지능형 추출:</strong>AI가 상품의 특징을
                                            분석하여 수집된 데이터를 바탕으로 핵심 시드 단어를
                                            생성합니다.
                                        </li>
                                        <li>
                                            <strong>실시간 태그 믹싱:</strong> 네이버 쇼핑
                                            상세페이지의 실제 등록 태그를 실시간으로 수집하여 조합에
                                            직접 반영합니다.
                                        </li>
                                        <li>
                                            <strong>타이틀 최적화:</strong> 상품명에서 불필요한
                                            브랜드/스토어명을 필터링하고 의미 있는 키워드만
                                            추출합니다.
                                        </li>
                                        <li>
                                            <strong>정밀 조합 엔진:</strong> 수집된 모든 재료를
                                            바탕으로 2어절/3어절 형태의 SEO 최적화 키워드를 대량으로
                                            생성합니다.
                                        </li>
                                    </ul>
                                </div>

                                <div className="guide-section">
                                    <h3>⚙️ 주요 조회 옵션 설명</h3>
                                    <div className="guide-grid">
                                        <div className="guide-item">
                                            <h4>2어절 / 3어절</h4>
                                            <p>
                                                검색 엔진 최적화(SEO)를 위해 키워드의 단어 수를
                                                고정합니다. 유입량이 많은 대형 키워드(2어절)와 구매
                                                전환율이 높은 롱테일 키워드(3어절)를 선택할 수
                                                있습니다.
                                            </p>
                                        </div>
                                        <div className="guide-item">
                                            <h4>통합 검색</h4>
                                            <p>
                                                네이버 통합 검색 결과 페이지에서 해당 상품이
                                                노출되는지 1위부터 정밀 추적합니다.
                                            </p>
                                        </div>
                                        <div className="guide-item">
                                            <h4>스토어명 제외</h4>
                                            <p>
                                                {
                                                    "고객사 브랜드명을 제외한 '순수 상품 키워드'만 골라내어 브랜드 파워가 아닌 키워드 자체의 경쟁력을 측정합니다."
                                                }
                                            </p>
                                        </div>
                                        <div className="guide-item">
                                            <h4>추출 조건 설정</h4>
                                            <p>
                                                목표 순위(예: 3위 이내)와 목표 개수(예: 10개)를
                                                설정하여 최상단에 노출 중인 우수 키워드만 전략적으로
                                                수집합니다.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="guide-section">
                                    <h3>📋 입력·한도</h3>
                                    <ul className="guide-list">
                                        <li>
                                            엑셀에서 복사(Ctrl+C) 후 표에 붙여넣기(Ctrl+V). 각 행은
                                            앞 두 열(순위키워드, URL)만 가져오며, 등급에 따라 한
                                            번에 붙여넣을 수 있는 행 수가 달라집니다.
                                        </li>
                                        <li>
                                            <strong>순위키워드</strong>와 <strong>URL</strong> 열은
                                            필수입니다. (표·엑셀 모두 앞쪽 두 열 순서)
                                        </li>
                                    </ul>
                                </div>

                            </div>
                        </div>
                    </div>
                )}
                {isHistoryOpen && (
                    <div
                        className="modal-overlay"
                        role="button"
                        tabIndex={0}
                        aria-label="닫기"
                        onClick={e => {
                            if (e.target === e.currentTarget) setIsHistoryOpen(false);
                        }}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
                                e.preventDefault();
                                setIsHistoryOpen(false);
                            }
                        }}
                    >
                        <div
                            className="modal-content modal-content--history"
                            style={{ maxWidth: '1200px' }}
                        >
                            <div className="modal-header">
                                <h2>전체 검색 히스토리</h2>
                                <button
                                    className="btn-close"
                                    onClick={() => setIsHistoryOpen(false)}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body modal-body--history">
                                <div className="integrated-search-history-table-wrap">
                                    <table className="search-table" style={{ fontSize: '0.75rem' }}>
                                        <thead>
                                            <tr>
                                                <th>시간</th>
                                                <th>상품명/URL</th>
                                                <th>상태</th>
                                                <th>조건</th>
                                                <th>결과</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historyData.map((h, i) => (
                                                <tr key={i}>
                                                    <td>
                                                        {new Date(h.createdAt).toLocaleString()}
                                                    </td>
                                                    <td>
                                                        <div
                                                            style={{
                                                                maxWidth: '200px',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                            }}
                                                        >
                                                            {h.productName}
                                                            <br />
                                                            <a
                                                                href={h.productUrl}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                style={{ fontSize: '0.65rem' }}
                                                            >
                                                                Link
                                                            </a>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span
                                                            className={`status-badge ${h.status === 'completed' ? 'success' : h.status === 'failed' ? 'idle' : 'loading'}`}
                                                        >
                                                            {h.status === 'completed'
                                                                ? '완료'
                                                                : h.status === 'failed'
                                                                  ? '실패'
                                                                  : '중지'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div style={{ lineHeight: '1.4' }}>
                                                            {h.wordCountOption} / {h.targetRank}위/
                                                            {h.targetCount}개<br />
                                                            <span
                                                                style={{
                                                                    fontSize: '0.65rem',
                                                                    color: '#6366f1',
                                                                }}
                                                            >
                                                                [
                                                                {h.includeRankingKeyword
                                                                    ? '순위키워드포함'
                                                                    : '순위키워드제외'}
                                                                ] [
                                                                {h.excludeStoreName
                                                                    ? '스토어제외'
                                                                    : '스토어명포함'}
                                                                ]
                                                            </span>
                                                            <br />
                                                            {h.rankingKeyword && (
                                                                <span
                                                                    style={{
                                                                        fontSize: '0.65rem',
                                                                        color: '#8b5cf6',
                                                                    }}
                                                                >
                                                                    RK: {h.rankingKeyword}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div style={{ fontSize: '0.7rem' }}>
                                                            풀: {h.generatedKeywordsCount}개<br />
                                                            추출:{' '}
                                                            {(h.finalFoundKeywords || [])
                                                                .map(k => k.keyword)
                                                                .join(', ')}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* 히스토리 페이징 컨트롤 */}
                                {historyPagination.totalPages > 1 && (
                                    <div
                                        className="pagination-wrapper"
                                        style={{
                                            marginTop: '20px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            gap: '5px',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <button
                                            className="btn-page"
                                            disabled={historyPagination.page === 1}
                                            onClick={() =>
                                                openHistoryModal(historyPagination.page - 1)
                                            }
                                        >
                                            &lt; 이전
                                        </button>
                                        {[...Array(historyPagination.totalPages)].map((_, idx) => {
                                            const p = idx + 1;
                                            // 현재 페이지 주변 5개만 표시하도록 로직 추가 가능
                                            if (
                                                p < historyPagination.page - 2 ||
                                                p > historyPagination.page + 2
                                            ) {
                                                if (p === 1 || p === historyPagination.totalPages)
                                                    return <span key={p}>...</span>;
                                                return null;
                                            }
                                            return (
                                                <button
                                                    key={p}
                                                    className={`btn-page ${historyPagination.page === p ? 'active' : ''}`}
                                                    onClick={() => openHistoryModal(p)}
                                                >
                                                    {p}
                                                </button>
                                            );
                                        })}
                                        <button
                                            className="btn-page"
                                            disabled={
                                                historyPagination.page ===
                                                historyPagination.totalPages
                                            }
                                            onClick={() =>
                                                openHistoryModal(historyPagination.page + 1)
                                            }
                                        >
                                            다음 &gt;
                                        </button>
                                        <span
                                            style={{
                                                marginLeft: '10px',
                                                fontSize: '0.8rem',
                                                color: '#666',
                                            }}
                                        >
                                            총 {historyPagination.total}건
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function IntegratedSearch() {
    return (
        <ProtectedMenuPage menuKey="integratedSearch">
            <IntegratedSearchInner />
            <KakaoFloatButton />
        </ProtectedMenuPage>
    );
}

export default IntegratedSearch;
